/**
 * Eigenbau: Der Kontextschalter der Vorlage — Kästchen, Name, Doppelpfeil.
 *
 * Tremors `Select` kam hier nicht weiter, und zwar nicht wegen Farben, sondern
 * wegen Aufbau:
 *
 * - Sein Auslöser trägt einen **Rahmen** (`border` plus `border-gray-300`). Die
 *   Vorlage hat an dieser Stelle keinen — die Zeile steht frei auf der Tafel und
 *   bekommt beim Überfahren nur eine Fläche.
 * - Sein fest eingebautes Remixicon lässt sich nicht passend ausrichten. Der
 *   eigene, schmale Auf-/Ab-Chevron sitzt unmittelbar hinter dem Projektnamen.
 * - Er setzt `justify-between`, schiebt das Zeichen also an den rechten Rand.
 *   Die Vorlage gibt der Beschriftung `flex: 0 1 auto`, damit der Chevron
 *   **direkt hinter dem Namen** sitzt.
 * - Das 21×21-Kästchen mit dem Projektkürzel gibt es dort gar nicht.
 *
 * Radix bleibt darunter: Auslöser, Liste, Tastatur, Fokusfalle und ARIA kommen
 * aus `@radix-ui/react-select` — dasselbe, worauf Tremors `Select` selbst sitzt.
 * Selbst geschrieben ist nur die Gestalt.
 *
 * Wie die Schiene ist der Wähler in beiden Themen dunkel und nimmt deshalb die
 * Tafel-Tinte, nicht `--tinte*`.
 */

import * as SelectPrimitive from "@radix-ui/react-select";
import { cx, focusRing } from "@/lib/utils";
import { Mikrolabel } from "@/components/entwurf/Mikrolabel";

export type Kontextprojekt = {
  id: string;
  kuerzel: string | null;
  name: string;
};

/** Das Kürzelkästchen: 21×21, Haarlinie als Ring, Mono in 9px. */
function Kuerzelkasten({ kuerzel }: { kuerzel: string }) {
  return (
    <span
      aria-hidden="true"
      className="flex size-[21px] flex-none items-center justify-center font-mono text-[9px] text-tafel-tinte2 shadow-[inset_0_0_0_1px_var(--tafel-tinte3)]"
    >
      {kuerzel}
    </span>
  );
}

export function Kontextwaehler({
  projekte,
  auswahl,
  alleWert,
  onWaehlen,
}: {
  projekte: readonly Kontextprojekt[];
  auswahl: string;
  /** Der Wert für „Alle Projekte". */
  alleWert: string;
  onWaehlen: (wert: string) => void;
}) {
  const gewaehlt = projekte.find((p) => p.id === auswahl);
  const name = gewaehlt?.name ?? "Alle Projekte";
  const kuerzel = gewaehlt?.kuerzel ?? null;
  const unbekannt = auswahl !== alleWert && !gewaehlt;

  return (
    <SelectPrimitive.Root value={auswahl} onValueChange={onWaehlen}>
      {/*
        `-ml-[9px]` bei `px-[9px]`: Der Text beginnt auf der Kante von
        „Kontext" und den Gruppentiteln, die Überfahrfläche reicht 9px darüber
        hinaus nach links. So macht es die Vorlage.

        Die Breite ist `100% + 9px`, nicht `+ 18px`. Die Vorlage gibt der Zeile
        gar keine Breite: Als Blockelement füllt sie ihren Kasten, und das
        negative Maß links macht sie um genau diese neun Pixel breiter — rechts
        bleibt sie auf der Kante der Spalte. Ein Knopf rechnet seine Breite
        dagegen aus dem Inhalt, deshalb muss sie hier stehen. Mit `+18px` ragte
        die Fläche auch rechts über die Spalte hinaus und schaute sichtbar über
        den Rand.
      */}
      <SelectPrimitive.Trigger
        aria-label="Projekt auswählen"
        className={cx(
          "-ml-[9px] mt-2.5 flex h-[34px] w-[calc(100%+9px)] items-center gap-2.5 px-[9px]",
          "text-left transition-colors duration-150 ease-aus hover:bg-tafel-wisch",
          focusRing,
        )}
      >
        {kuerzel ? <Kuerzelkasten kuerzel={kuerzel} /> : null}
        {/*
          `flex: 0 1 auto` statt `flex-1`: Der Name nimmt nur den Platz, den er
          braucht, und der Chevron folgt ihm unmittelbar. Mit `flex-1` stünde
          das Zeichen am rechten Rand der Schiene.
        */}
        <span className="min-w-0 flex-[0_1_auto] truncate text-[13.5px] font-normal text-tafel-tinte">
          <SelectPrimitive.Value>
            {unbekannt ? "Wird geladen …" : name}
          </SelectPrimitive.Value>
        </span>
        <SelectPrimitive.Icon asChild>
          {/*
            Der kompakte Auf-/Ab-Chevron sitzt direkt hinter dem Namen und
            macht sichtbar, dass sich der Projektkontext wechseln lässt.
          */}
          <svg
            width="10"
            height="14"
            viewBox="0 0 10 14"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="square"
            strokeLinejoin="miter"
            aria-hidden="true"
            className="-ml-px flex-none text-tafel-tinte3"
          >
            <path d="m1.5 5 3.5-3 3.5 3" />
            <path d="m1.5 9 3.5 3 3.5-3" />
          </svg>
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>

      <SelectPrimitive.Portal>
        {/*
          Die Liste bleibt auf der Tafel. Eine helle Liste, die an einer
          schwarzen Leiste klebt, sieht aus wie ein fremdes Fenster — derselbe
          Grund wie beim Benutzermenü.
        */}
        <SelectPrimitive.Content
          position="popper"
          sideOffset={6}
          className={cx(
            "z-50 max-h-[--radix-select-content-available-height] min-w-[236px]",
            "overflow-y-auto border border-tafel-linie bg-tafel text-tafel-tinte",
          )}
        >
          <SelectPrimitive.Viewport>
            {unbekannt ? (
              <SelectPrimitive.Item
                value={auswahl}
                disabled
                className="flex h-9 items-center px-3 text-[13px] font-light text-tafel-tinte3"
              >
                <SelectPrimitive.ItemText>
                  Wird geladen …
                </SelectPrimitive.ItemText>
              </SelectPrimitive.Item>
            ) : null}
            <Eintrag wert={alleWert}>Alle Projekte</Eintrag>
            {projekte.map((projekt) => (
              <Eintrag key={projekt.id} wert={projekt.id}>
                {projekt.kuerzel ? (
                  <Mikrolabel grad={11} className="w-9 flex-none text-tafel-tinte3">
                    {projekt.kuerzel}
                  </Mikrolabel>
                ) : null}
                <span className="min-w-0 truncate">{projekt.name}</span>
              </Eintrag>
            ))}
          </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
}

function Eintrag({
  wert,
  children,
}: {
  wert: string;
  children: React.ReactNode;
}) {
  return (
    <SelectPrimitive.Item
      value={wert}
      className={cx(
        "flex h-9 cursor-pointer items-center gap-2.5 px-3 text-[13px] font-light text-tafel-tinte outline-none",
        "data-[highlighted]:bg-tafel-wisch data-[highlighted]:text-tafel-akzent",
        "data-[state=checked]:font-medium data-[state=checked]:text-tafel-akzent",
      )}
    >
      <SelectPrimitive.ItemText asChild>
        <span className="flex min-w-0 items-center gap-2.5">{children}</span>
      </SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  );
}
